import { Container, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {
  createLinkedinAccountExternal,
  get2FAScreenshotExternal,
  getCaptchaExternal,
  getCountryCodesExternal,
  getLinkedinAccountByIdExternal,
  getThirdPartyScreenshotExternal,
  logoutLinkedinAccountExternal,
  resetLinkedinAccountExternal,
  setThirdPartyTextExternal,
  solveCaptchaExternal,
  syncLinkedinAccountExternal,
  thirdPartyClickExternal,
  updateLinkedinAccountContractExternal,
  updateLinkedinAccountEmailCredentialsExternal,
  updateLinkedinAccountExternal,
  updateLinkedinAccountExternalWithPayload,
  verifyLinkedinAccountExternal,
} from 'API/linkedin-accounts-external';
import { ErrorMessages, LinkedinTypes, ProductGroups } from 'enums';
import ErrorDialog from 'features/ui/app-layout/error-dialog';
import PaiButton from 'features/ui/base-components/pai-button';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import useErrorStore from 'stores/error-store';
import useLimitReachedStore from 'stores/limit-reached-store';
import { makeStyles } from 'tss-react/mui';
import { OptionType } from '../../ui/base-components/pai-auto-complete';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import AccountAlreadyExistsDialog from './account-already-exists-dialog';
import AccountAlreadySyncedDialog from './account-already-synced-dialog';
import AppSyncDialog, { } from './app-sync-dialog/app-sync-dialog';
import { AppSyncErrors, AppSyncSteps, stepOrder, SyncProcessingStatuses } from './app-sync-dialog/app-sync-dialog.types';
import EditEmailCredentialsDialog from './edit-email-credentials-dialog';
import EditLinkedinAccountGeneralSettingsDialog from './edit-linkedin-account-general-settings-dialog';
import EditLinkedinAccountLoginInformationDialog from './edit-linkedin-account-login-information-dialog';
import FairUsePauseDialog from './fair-use-pause-dialog';
import LinkedinAccountItem from './linkedin-account-item';
import {
  CountryCode,
  EmailAccountTypes,
  EmailCredentialErrors,
  EmailCredentials,
  Encryptions,
  LinkedinAccount,
  LinkedinAccountStatuses,
  OAuthCredentials, normalizeAccountStatus } from './linkedin-accounts.types';
import OnboardingIncompleteDialog from './onboarding-incomplete-dialog';
import RecruiterParallelLoginDialog from './recruiter-parallel-login-dialog';
import TokenExpiredDialog from './token-expired-dialog';
import TwoFaNotEnabledDialog from './two-fa-not-enabled-dialog';
import WaitBeforeSyncDialog from './wait-before-sync-dialog';
import ManageEmailSendersDialog from './manage-email-senders-dialog';

const LinkedinAccountExternal: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('token');
  const accountId = searchParams.get('id');

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const handleOpenLimitReachedDialog = useLimitReachedStore((state) => state.handleOpenLimitReachedDialog);

  const [allCountryCodes, setAllCountryCodes] = useState<CountryCode[]>([]);

  const [linkedinAccount, setLinkedinAccount] = useState<LinkedinAccount>();
  const [linkedinAccountsLoading, setLinkedinAccountsLoading] = useState(false);
  const [appSyncDialog, setAppSyncDialog] = useState<{
    open: boolean;
    isStarterExperience: boolean;
    loading: {
      create: boolean;
      sync: boolean;
      verifyPin: boolean;
      getCaptcha: boolean;
      solveCaptcha: boolean;
      getThirdPartyScreenshot: boolean;
      thirdPartyClick: boolean;
      update: boolean;
    };
    syncProcessingStatus: SyncProcessingStatuses;
    error: AppSyncErrors | null;
    activeStepIndex: number;
    selectedLinkedinProduct: LinkedinTypes;
    selectedCountry: OptionType;
    selectedCity: OptionType;
    userName: string;
    password: string;
    linkedinAccount: LinkedinAccount | null;
    verificationCode: string;
    captcha: string | null;
    markerPosition: { x: number; y: number; percentageX: number; percentageY: number } | null;
    fetchCaptchaIntervalId: NodeJS.Timeout | null;
    twoFaScreenshot: string | null;
    fetch2FAScreenshotIntervalId: NodeJS.Timeout | null;
    thirdPartyScreenshot: string | null;
    fetchThirdPartyScreenshotIntervalId: NodeJS.Timeout | null;
    thirdPartyText: string;
    linkedinContracts: string[];
    selectedContract: string | null;
  }>({
    open: false,
    isStarterExperience: false,
    activeStepIndex: 0,
    loading: {
      create: false,
      sync: false,
      verifyPin: false,
      getCaptcha: false,
      solveCaptcha: false,
      getThirdPartyScreenshot: false,
      thirdPartyClick: false,
      update: false,
    },
    syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
    error: null,
    selectedLinkedinProduct: LinkedinTypes.LINKEDIN,
    selectedCountry: { label: '', value: '' },
    selectedCity: { label: '', value: '' },
    userName: '',
    password: '',
    linkedinAccount: null,
    verificationCode: '',
    captcha: null,
    markerPosition: null,
    fetchCaptchaIntervalId: null,
    twoFaScreenshot: null,
    fetch2FAScreenshotIntervalId: null,
    thirdPartyScreenshot: null,
    fetchThirdPartyScreenshotIntervalId: null,
    thirdPartyText: '',
    linkedinContracts: [],
    selectedContract: null,
  });
  const [accountAlreadyExistsDialog, setAccountAlreadyExistsDialog] = useState<{ open: boolean; userEmail: string | null }>({
    open: false,
    userEmail: null,
  });
  const [editLinkedinAccountGeneralSettingsDialog, setEditLinkedinAccountGeneralSettingsDialog] = useState<{
    open: boolean;
    account: LinkedinAccount | null;
    selectedCountry: OptionType;
    selectedCity: OptionType;
    linkedinProduct: LinkedinTypes;
    loading: boolean;
  }>({
    open: false,
    account: null,
    selectedCountry: { label: '', value: '' },
    selectedCity: { label: '', value: '' },
    linkedinProduct: LinkedinTypes.LINKEDIN,
    loading: false,
  });
  const [editLinkedinAccountLoginInformationDialog, setEditLinkedinAccountLoginInformationDialog] = useState<{
    open: boolean;
    account: LinkedinAccount | null;
    userName: string;
    password: string;
    loading: boolean;
  }>({
    open: false,
    account: null,
    userName: '',
    password: '',
    loading: false,
  });
  const [waitBeforeSyncDialog, setWaitBeforeSyncDialog] = useState<{
    open: boolean;
    waitingTime: { hours: number; minutes: number } | null;
  }>({ open: false, waitingTime: null });
  const [accountAlreadySyncedDialog, setAccountAlreadySyncedDialog] = useState<{ open: boolean }>({ open: false });
  const [editEmailCredentialsDialog, setEditEmailCredentialsDialog] = useState<{
    open: boolean;
    type: EmailAccountTypes;
    smtp: EmailCredentials;
    imap: EmailCredentials;
    oauth: OAuthCredentials;
    account: LinkedinAccount | null;
    error: EmailCredentialErrors | undefined;
    accessRevoked: boolean;
    loading: boolean;
  }>({
    open: false,
    type: EmailAccountTypes.OTHER,
    smtp: {
      server: '',
      port: '',
      encryption: Encryptions.NONE,
      username: '',
      password: '',
    },
    imap: {
      server: '',
      port: '',
      encryption: Encryptions.NONE,
      username: '',
      password: '',
    },
    oauth: {
      email: '',
      code: '',
    },
    account: null,
    error: undefined,
    accessRevoked: false,
    loading: false,
  });
  const [recruiterParallelLoginDialog, setRecruiterParallelLoginDialog] = useState<{
    open: boolean;
    loading: boolean;
    account: LinkedinAccount | null;
  }>({
    open: false,
    loading: false,
    account: null,
  });
  const [onboardingIncompleteDialog, setOnboardingIncompleteDialog] = useState<{
    open: boolean;
    loading: boolean;
    account: LinkedinAccount | null;
  }>({
    open: false,
    loading: false,
    account: null,
  });
  const [fairUsePauseDialog, setFairUsePauseDialog] = useState<{
    open: boolean;
  }>({
    open: false,
  });
  const [twoFaNotEnabledDialog, setTwoFaNotEnabledDialog] = useState<{ open: boolean; account: LinkedinAccount | null }>({
    open: false,
    account: null,
  });

  const [tokenExpiredDialog, setTokenExpiredDialog] = useState<{ open: boolean }>({ open: false });

  const [logoutLoading, setLogoutLoading] = useState<string[]>([]);

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const paiLogo = '/dashboard/images/logos/product_logo_white.svg';

  const [manageEmailSendersDialog, setManageEmailSendersDialog] = useState<{ open: boolean; account: LinkedinAccount | null }>(
    { open: false, account: null }
  );

  const fetchLinkedinAccountById = async (disableLoading?: boolean) => {
    if (_.isNil(accountId)) return;
    if (_.isNil(token)) return;

    if (!disableLoading) {
      setLinkedinAccountsLoading(true);
    }

    const res = await getLinkedinAccountByIdExternal(token, accountId);

    if (!res.error) {
      setLinkedinAccount(res);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else {
        handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id', 'get');
      }
    }
    setLinkedinAccountsLoading(false);
  };

  const fetchCountryCodes = async () => {
    if (_.isNil(token)) return;
    const res = await getCountryCodesExternal(token);

    if (!res.error) {
      setAllCountryCodes(res.countries || []);
    } else {
      if (res.error.message === 'You are not allowed to use this product!' || res.error.message === 'Not enough credits!') {
        handleOpenLimitReachedDialog();
      } else if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else {
        handleOpenErrorDialog(res.error, '/api/linkedinAccounts/countrycodes', 'get');
      }
    }
  };

  useEffect(() => {
    fetchCountryCodes();

    if (!_.isNil(accountId)) {
      fetchLinkedinAccountById();

      const intervalId = setInterval(() => {
        fetchLinkedinAccountById(true);
      }, 60 * 1000);

      return () => {
        clearInterval(intervalId);
      };
    }

    return undefined;
  }, [token, accountId]);

  /**
   * Edit linkedin account general settings dialog handlers
   */

  const handleOpenEditLinkedinAccountGeneralSettingsDialog = (account: LinkedinAccount) => {
    const foundCountry = allCountryCodes.find((country) => country.code === account.countryName);
    let userCountry = { label: '', value: '' };
    let userCity = { label: '', value: '' };

    if (!_.isNil(foundCountry)) {
      userCountry = { label: foundCountry.name, value: foundCountry.code };

      const foundCity = foundCountry.cities.options.find((city) => city.code === account.cityName);

      if (!_.isNil(foundCity)) {
        userCity = { label: foundCity.name, value: foundCity.code };
      }
    }

    return () => {
      setEditLinkedinAccountGeneralSettingsDialog((prev) => ({
        ...prev,
        open: true,
        account,
        selectedCountry: userCountry,
        selectedCity: userCity,
        linkedinProduct: account.liProductType,
      }));
    };
  };

  const handleCloseEditLinkedinAccountGeneralSettingsDialog = () => {
    setEditLinkedinAccountGeneralSettingsDialog({
      open: false,
      account: null,
      selectedCountry: { label: '', value: '' },
      selectedCity: { label: '', value: '' },
      linkedinProduct: LinkedinTypes.LINKEDIN,
      loading: false,
    });
  };

  const handleEditCountryChange = (event: React.ChangeEvent<any>, newValue: OptionType) => {
    setEditLinkedinAccountGeneralSettingsDialog((prev) => ({ ...prev, selectedCountry: newValue }));
  };

  const handleEditCityChange = (event: React.ChangeEvent<any>, newValue: OptionType) => {
    setEditLinkedinAccountGeneralSettingsDialog((prev) => ({ ...prev, selectedCity: newValue }));
  };

  const handleEditLinkedinProductChange = (value: LinkedinTypes) => {
    return () => {
      setEditLinkedinAccountGeneralSettingsDialog((prev) => ({ ...prev, linkedinProduct: value }));
    };
  };

  const handleEditLinkedinAccountGeneralSettings = async () => {
    if (!_.isNil(editLinkedinAccountGeneralSettingsDialog.account)) {
      if (_.isNil(token)) return;
      setEditLinkedinAccountGeneralSettingsDialog((prev) => ({ ...prev, loading: true }));

      const payload = {
        countryName: editLinkedinAccountGeneralSettingsDialog.selectedCountry.value,
        cityName: editLinkedinAccountGeneralSettingsDialog.selectedCity.value,
        liProductType: editLinkedinAccountGeneralSettingsDialog.linkedinProduct,
      };

      const res = await updateLinkedinAccountExternalWithPayload(token, editLinkedinAccountGeneralSettingsDialog.account._id, payload);

      if (!res.error) {
        fetchLinkedinAccountById(true);
        handleCloseEditLinkedinAccountGeneralSettingsDialog();
      } else {
        if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
          handleOpenTokenExpiredDialog();
        } else {
          handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id', 'put');
        }

        setEditLinkedinAccountGeneralSettingsDialog((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  /**
   * Edit linkedin account login information dialog handlers
   */

  const handleOpenEditLinkedinAccountLoginInformationDialog = (account: LinkedinAccount) => {
    return () => {
      setEditLinkedinAccountLoginInformationDialog((prev) => ({ ...prev, open: true, account, userName: account.userEmail, password: '' }));
    };
  };

  const handleCloseEditLinkedinAccountLoginInformationDialog = () => {
    setEditLinkedinAccountLoginInformationDialog({ open: false, account: null, userName: '', password: '', loading: false });
  };

  const handleEditUserNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLoginInformationDialog((prev) => ({ ...prev, userName: event.target.value }));
  };

  const handleEditPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLoginInformationDialog((prev) => ({ ...prev, password: event.target.value }));
  };

  const handleEditLinkedinAccountLoginInformation = async () => {
    if (!_.isNil(editLinkedinAccountLoginInformationDialog.account)) {
      if (_.isNil(token)) return;
      setEditLinkedinAccountLoginInformationDialog((prev) => ({ ...prev, loading: true }));

      const payload = {
        userEmail: editLinkedinAccountLoginInformationDialog.userName,
        userPassword: editLinkedinAccountLoginInformationDialog.password,
      };

      const res = await updateLinkedinAccountExternalWithPayload(token, editLinkedinAccountLoginInformationDialog.account._id, payload);

      if (!res.error) {
        fetchLinkedinAccountById(true);
        handleCloseEditLinkedinAccountLoginInformationDialog();
      } else {
        if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
          handleOpenTokenExpiredDialog();
        } else {
          handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id', 'put');
        }

        setEditLinkedinAccountLoginInformationDialog((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  /**
   * Wait before sync dialog handlers
   */

  const handleOpenWaitBeforeSyncDialog = (waitingTime: { hours: number; minutes: number }) => {
    setWaitBeforeSyncDialog({ open: true, waitingTime });
  };

  const handleCloseWaitBeforeSyncDialog = () => {
    setWaitBeforeSyncDialog((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!waitBeforeSyncDialog.open) {
      setTimeout(() => {
        setWaitBeforeSyncDialog((prev) => ({ ...prev, waitingTime: null }));
      }, 225);
    }
  }, [waitBeforeSyncDialog.open]);

  /**
   * Account already synced dialog handlers
   */

  const handleOpenAccountAlreadySyncedDialog = () => {
    setAccountAlreadySyncedDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseAccountAlreadySyncedDialog = () => {
    setAccountAlreadySyncedDialog({ open: false });
  };

  /**
   * Edit email credentials dialog handlers
   */

  const handleEmailAccountTypeChange = (value: string) => {
    return () => {
      setEditEmailCredentialsDialog((prev) => ({ ...prev, type: value as EmailAccountTypes }));
    };
  };

  const handleOpenEditEmailCredentialsDialog = (account: LinkedinAccount) => {
    return () => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        open: true,
        account,
        accessRevoked: !!account.emailConfig?.accessRevoked,
        type: !_.isNil(account.emailConfig?.smtp)
          ? EmailAccountTypes.OTHER
          : !_.isNil(account.emailConfig?.oauth)
            ? EmailAccountTypes.MS_O365
            : !_.isNil(account.emailConfig?.gmailOauth)
              ? EmailAccountTypes.GMAIL
              : EmailAccountTypes.OTHER,
        smtp: account.emailConfig?.smtp
          ? { ...account.emailConfig.smtp, password: '' }
          : {
            server: '',
            port: '',
            encryption: Encryptions.NONE,
            username: '',
            password: '',
          },
        imap: account.emailConfig?.imap
          ? { ...account.emailConfig.imap, password: '' }
          : {
            server: '',
            port: '',
            encryption: Encryptions.NONE,
            username: '',
            password: '',
          },
        oauth: account.emailConfig?.oauth
          ? { ...account.emailConfig.oauth, code: '' }
          : account.emailConfig?.gmailOauth
            ? { ...account.emailConfig.gmailOauth, code: '' }
            : {
              email: '',
              code: '',
            },
      }));
    };
  };

  const handleCloseEditEmailCredentialsDialog = () => {
    setEditEmailCredentialsDialog((prev) => ({
      ...prev,
      open: false,
      loading: false,
    }));
  };

  useEffect(() => {
    if (!editEmailCredentialsDialog.open) {
      setTimeout(() => {
        setEditEmailCredentialsDialog((prev) => ({
          ...prev,
          type: EmailAccountTypes.OTHER,
          smtp: {
            server: '',
            port: '',
            encryption: Encryptions.NONE,
            username: '',
            password: '',
          },
          imap: {
            server: '',
            port: '',
            encryption: Encryptions.NONE,
            username: '',
            password: '',
          },
          oauth: {
            email: '',
            code: '',
          },
          account: null,
          error: undefined,
          accessRevoked: false,
        }));
      }, 225);
    }
  }, [editEmailCredentialsDialog.open]);

  const handleSmtpEmailCredentialsChange = (field: keyof EmailCredentials) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        smtp: {
          ...prev.smtp,
          [field]: event.target.value,
        },
      }));
    };
  };

  const handleSmtpEncryptionChange = (value: string) => {
    return () => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        smtp: {
          ...prev.smtp,
          encryption: value as Encryptions,
        },
      }));
    };
  };

  const handleImapEmailCredentialsChange = (field: keyof EmailCredentials) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        imap: {
          ...prev.imap,
          [field]: event.target.value,
        },
      }));
    };
  };

  const handleImapEncryptionChange = (value: string) => {
    return () => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        imap: {
          ...prev.imap,
          encryption: value as Encryptions,
        },
      }));
    };
  };

  const handleOAuthEmailCredentialsChange = (field: keyof OAuthCredentials) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        oauth: {
          ...prev.oauth,
          [field]: event.target.value,
        },
      }));
    };
  };

  const handleAcceptEmailCredentials = async () => {
    if (!_.isNil(editEmailCredentialsDialog.account)) {
      if (_.isNil(token)) return;
      setEditEmailCredentialsDialog((prev) => ({ ...prev, loading: true, error: undefined }));

      const account = editEmailCredentialsDialog.account;
      const res =
        editEmailCredentialsDialog.type === EmailAccountTypes.MS_O365 || editEmailCredentialsDialog.type === EmailAccountTypes.GMAIL
          ? await updateLinkedinAccountEmailCredentialsExternal(
            token,
            account._id,
            editEmailCredentialsDialog.type,
            undefined,
            undefined,
            editEmailCredentialsDialog.oauth
          )
          : await updateLinkedinAccountEmailCredentialsExternal(
            token,
            account._id,
            editEmailCredentialsDialog.type,
            editEmailCredentialsDialog.smtp,
            editEmailCredentialsDialog.imap,
            undefined
          );

      if (!res.error) {
        fetchLinkedinAccountById(true);
        handleCloseEditEmailCredentialsDialog();
      } else {
        if (res.error.code === 400 && res.error.message?.includes('Server details are incorrect. Please check your SMTP settings.')) {
          setEditEmailCredentialsDialog((prev) => ({ ...prev, loading: false, error: EmailCredentialErrors.SMTP }));
        } else if (res.error.code === 400 && res.error.message?.includes('Server details are incorrect. Please check your IMAP settings.')) {
          setEditEmailCredentialsDialog((prev) => ({ ...prev, loading: false, error: EmailCredentialErrors.IMAP }));
        } else if (res.error.code === 400 && res.error.message?.includes('User credentials are incorrect.')) {
          setEditEmailCredentialsDialog((prev) => ({ ...prev, loading: false, error: EmailCredentialErrors.CREDENTIALS }));
        } else if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
          handleOpenTokenExpiredDialog();
        } else {
          handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id/saveEmailConfig', 'post');
        }

        setEditEmailCredentialsDialog((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  /**
   * Recruiter parallel login dialog handlers
   */

  const handleOpenRecruiterParallelLoginDialog = (account: LinkedinAccount) => {
    return () => {
      setRecruiterParallelLoginDialog((prev) => ({ ...prev, open: true, account }));
    };
  };

  const handleCloseRecruiterParallelLoginDialog = () => {
    setRecruiterParallelLoginDialog({ open: false, loading: false, account: null });
  };

  const handleReSynchronizeLinkedinAccountFromParallelLogin = () => {
    const { account } = recruiterParallelLoginDialog;

    if (!_.isNil(account)) {
      handleOpenAppSyncAndSynchronizeLiAccount(account)();
      handleCloseRecruiterParallelLoginDialog();
    }
  };

  /**
   * Onboarding incomplete dialog handlers
   */

  const handleOpenOnboardingIncompleteDialog = (account: LinkedinAccount) => {
    return () => {
      setOnboardingIncompleteDialog((prev) => ({ ...prev, open: true, account }));
    };
  };

  const handleCloseOnboardingIncompleteDialog = () => {
    setOnboardingIncompleteDialog({ open: false, loading: false, account: null });
  };

  const handleReSynchronizeLinkedinAccountFromOnboardingIncomplete = () => {
    const { account } = onboardingIncompleteDialog;

    if (!_.isNil(account)) {
      handleOpenAppSyncAndSynchronizeLiAccount(account)();
      handleCloseOnboardingIncompleteDialog();
    }
  };

  /**
   * Fair use pause dialog handlers
   */

  const handleOpenFairUsePauseDialog = () => {
    setFairUsePauseDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseFairUsePauseDialog = () => {
    setFairUsePauseDialog({ open: false });
  };

  /**
   * 2FA not enabled dialog
   */

  const handleOpenTwoFaNotEnabledDialog = (account: LinkedinAccount) => {
    return () => {
      setTwoFaNotEnabledDialog((prev) => ({ ...prev, open: true, account }));
    };
  };

  const handleCloseTwoFaNotEnabledDialog = () => {
    setTwoFaNotEnabledDialog({ open: false, account: null });
  };

  const handleReSynchronizeLinkedinAccountFromTwoFaNotEnabled = () => {
    const { account } = twoFaNotEnabledDialog;

    if (!_.isNil(account)) {
      handleOpenAppSyncAndSynchronizeLiAccount(account)();
      handleCloseTwoFaNotEnabledDialog();
    }
  };

  /**
   * Token expired dialog handlers
   */

  const handleOpenTokenExpiredDialog = () => {
    setTokenExpiredDialog({ open: true });
  };

  const handleCloseTokenExpiredDialog = () => {
    setTokenExpiredDialog({ open: false });
  };

  /**
   * App sync dialog handlers
   */

  const handleOpenAppSyncDialog = (isStarterExperience: boolean) => {
    return () => {
      setAppSyncDialog((prev) => ({ ...prev, open: true, isStarterExperience }));
    };
  };

  const handleOpenAppSyncAndSynchronizeLiAccount = (linkedinAccount: LinkedinAccount) => {
    return () => {
      if (_.isNil(linkedinAccount)) return;
      const selectedLinkedinProduct = linkedinAccount.liProductType;

      setAppSyncDialog((prev) => ({ ...prev, open: true, isStarterExperience: false, linkedinAccount, selectedLinkedinProduct }));

      handleSynchronizeLinkedinAccountFromAppSync(linkedinAccount);
    };
  };

  const handleOpenAppSyncAndVerifyPin = (linkedinAccount: LinkedinAccount) => {
    return () => {
      if (_.isNil(linkedinAccount)) return;

      const selectedLinkedinProduct = linkedinAccount.liProductType;
      const syncStepIndex = stepOrder.findIndex((step) => step === AppSyncSteps.SYNCHRONIZING);
      setAppSyncDialog((prev) => ({
        ...prev,
        open: true,
        isStarterExperience: false,
        activeStepIndex: syncStepIndex,
        syncProcessingStatus: SyncProcessingStatuses.TWO_FA,
        selectedLinkedinProduct,
        linkedinAccount,
        error: null,
      }));
    };
  };

  const handleOpenAppSyncThirdPartyWebsite = (linkedinAccount: LinkedinAccount) => {
    return () => {
      if (_.isNil(linkedinAccount)) return;

      const selectedLinkedinProduct = linkedinAccount.liProductType;
      const syncStepIndex = stepOrder.findIndex((step) => step === AppSyncSteps.SYNCHRONIZING);
      setAppSyncDialog((prev) => ({
        ...prev,
        open: true,
        isStarterExperience: false,
        activeStepIndex: syncStepIndex,
        syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY,
        selectedLinkedinProduct,
        linkedinAccount,
        error: null,
      }));
      fetchThirdPartyScreenshotForAppSync(linkedinAccount._id);
    };
  };

  const handleOpenAppSyncAndSolveCaptcha = (linkedinAccount: LinkedinAccount) => {
    return () => {
      if (_.isNil(linkedinAccount)) return;

      const selectedLinkedinProduct = linkedinAccount.liProductType;
      const syncStepIndex = stepOrder.findIndex((step) => step === AppSyncSteps.SYNCHRONIZING);
      setAppSyncDialog((prev) => ({
        ...prev,
        open: true,
        isStarterExperience: false,
        activeStepIndex: syncStepIndex,
        syncProcessingStatus: SyncProcessingStatuses.CAPTCHA,
        selectedLinkedinProduct,
        linkedinAccount,
        error: null,
      }));
      fetchCaptchaForAppSync(linkedinAccount._id);
    };
  };

  const handleOpenAppSyncAndCorrectAccessData = (linkedinAccount: LinkedinAccount) => {
    return () => {
      if (_.isNil(linkedinAccount)) return;

      const selectedLinkedinProduct = linkedinAccount.liProductType;
      const userName = linkedinAccount.userEmail;

      const foundCountry = allCountryCodes.find((country) => country.code === linkedinAccount.countryName);
      let userCountry = { label: '', value: '' };
      let userCity = { label: '', value: '' };

      if (!_.isNil(foundCountry)) {
        userCountry = { label: foundCountry.name, value: foundCountry.code };

        const foundCity = foundCountry.cities.options.find((city) => city.code === linkedinAccount.cityName);

        if (!_.isNil(foundCity)) {
          userCity = { label: foundCity.name, value: foundCity.code };
        }
      }

      setAppSyncDialog((prev) => ({
        ...prev,
        open: true,
        isStarterExperience: false,
        activeStepIndex: -1,
        selectedLinkedinProduct,
        selectedCountry: userCountry,
        selectedCity: userCity,
        userName,
        linkedinAccount,
        error: AppSyncErrors.WRONG_CREDENTIALS,
      }));
    };
  };

  const handleCloseAppSyncDialog = () => {
    if (!_.isNil(intervalIdRef.current)) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }

    const { fetchCaptchaIntervalId, fetchThirdPartyScreenshotIntervalId } = appSyncDialog;

    if (!_.isNil(fetchCaptchaIntervalId)) {
      clearInterval(fetchCaptchaIntervalId);
    }

    if (!_.isNil(fetchThirdPartyScreenshotIntervalId)) {
      clearInterval(fetchThirdPartyScreenshotIntervalId);
    }

    setAppSyncDialog({
      open: false,
      isStarterExperience: false,
      activeStepIndex: 0,
      loading: {
        create: false,
        sync: false,
        verifyPin: false,
        getCaptcha: false,
        solveCaptcha: false,
        getThirdPartyScreenshot: false,
        thirdPartyClick: false,
        update: false,
      },
      syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      error: null,
      selectedLinkedinProduct: LinkedinTypes.LINKEDIN,
      selectedCountry: { label: '', value: '' },
      selectedCity: { label: '', value: '' },
      userName: '',
      password: '',
      linkedinAccount: null,
      verificationCode: '',
      captcha: null,
      markerPosition: null,
      fetchCaptchaIntervalId: null,
      twoFaScreenshot: null,
      fetch2FAScreenshotIntervalId: null,
      thirdPartyScreenshot: null,
      fetchThirdPartyScreenshotIntervalId: null,
      thirdPartyText: '',
      linkedinContracts: [],
      selectedContract: null,
    });
  };

  const increaseActiveStepIndex = () => {
    setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: Math.min(prev.activeStepIndex + 1, stepOrder.length - 1) }));
  };

  const decreaseActiveStepIndex = () => {
    setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: Math.max(prev.activeStepIndex - 1, 0) }));
  };

  const goToSyncStep = () => {
    const syncStepIndex = stepOrder.findIndex((step) => step === AppSyncSteps.SYNCHRONIZING);
    setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: syncStepIndex, syncProcessingStatus: SyncProcessingStatuses.PROCESSING, error: null }));
  };

  const handleAppSyncLinkedinProductChange = (value: string) => {
    return () => {
      setAppSyncDialog((prev) => ({ ...prev, selectedLinkedinProduct: value as LinkedinTypes }));
    };
  };

  const handleAppSyncCountryChange = (event: React.ChangeEvent<any>, newValue: OptionType) => {
    setAppSyncDialog((prev) => ({ ...prev, selectedCountry: newValue }));
  };

  const handleAppSyncCityChange = (event: React.ChangeEvent<any>, newValue: OptionType) => {
    setAppSyncDialog((prev) => ({ ...prev, selectedCity: newValue }));
  };

  const handleAppSyncUserNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAppSyncDialog((prev) => ({ ...prev, userName: event.target.value }));
  };

  const handleAppSyncPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAppSyncDialog((prev) => ({ ...prev, password: event.target.value }));
  };

  const handleCreateAndSynchronizeLiAccount = async () => {
    if (_.isNil(token)) return;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, create: true } }));

    const { selectedLinkedinProduct, selectedCountry, selectedCity, userName, password } = appSyncDialog;

    const res = await createLinkedinAccountExternal(token, selectedCountry.value, selectedCity.value, userName, password, selectedLinkedinProduct);

    if (!res.error) {
      setLinkedinAccount(res);
      setSearchParams((prev) => {
        prev.set('id', res._id);
        return prev;
      });
      setAppSyncDialog((prev) => ({
        ...prev,
        userName: '',
        password: '',
        linkedinAccount: res,
      }));
      handleSynchronizeLinkedinAccountFromAppSync(res);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else if (
        res.error.message === 'You are not allowed to use this product!' ||
        res.error.message === 'Not enough credits!' ||
        res.error.message === 'Not enough credits to create an account!'
      ) {
        handleOpenLimitReachedDialog();
      } else if (res.error.message === 'Account already exists!') {
        handleOpenAccountAlreadyExistsDialog(userName);
      } else {
        handleOpenErrorDialog(res.error, '/api/linkedinAccounts/addLinkedInAccount', 'post');
      }
    }
    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, create: false } }));
  };

  const handleSynchronizeLinkedinAccountFromAppSync = async (linkedinAccount: LinkedinAccount) => {
    if (_.isNil(token)) return;
    if (_.isNil(linkedinAccount)) return;
    const linkedinAccountId = linkedinAccount._id;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, sync: true } }));
    goToSyncStep();

    const res = await syncLinkedinAccountExternal(token, linkedinAccountId);

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, sync: false } }));

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountByIdExternal(token, linkedinAccountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccount((prev) => ({ ...prev!, status: resLinkedinAccount.status }));
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (normalizeAccountStatus(resLinkedinAccount.status)) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              fetch2FAScreenshotForAppSync(linkedinAccountId);
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.TWO_FA }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              fetchCaptchaForAppSync(linkedinAccountId);
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                linkedinContracts: resLinkedinAccount.linkedinContracts,
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.WRONG_CREDENTIALS }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.WRONG_PIN }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.TIMED_OUT_CAPTCHA }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.ONBOARDING_INCOMPLETE }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.TWO_FA_NOT_ENABLED }));
              break;
            default:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.UNKNOWN }));
              break;
          }
        } else {
          if (resLinkedinAccount.error.code === 401 && resLinkedinAccount.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
            handleOpenTokenExpiredDialog();
          } else {
            handleOpenErrorDialog(resLinkedinAccount?.error, '/api/linkedinAccounts/:id', 'get');
          }
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else if (res.error.message?.includes('Please wait for')) {
        const errorMessage = res.error.message;
        const timeRegex = /Please wait for (\d+):(\d+)h, before trying to login again!/;
        const match = timeRegex.exec(errorMessage);

        if (match) {
          const hours = parseInt(match[1], 10);
          const minutes = parseInt(match[2], 10);

          handleOpenWaitBeforeSyncDialog({ hours, minutes });
        }
      } else if (res.error.message === ErrorMessages.ACCOUNT_ALREADY_IN_QUEUE) {
        handleOpenAccountAlreadySyncedDialog();
      } else {
        handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id/sync', 'post');
      }
    }
  };

  const handleAppSyncVerificationCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAppSyncDialog((prev) => ({ ...prev, verificationCode: event.target.value }));
  };

  const handleVerifyLinkedinAccountFromAppSync = async (mode: 'pin' | 'approved' = 'pin') => {
    if (_.isNil(token)) return;
    const { linkedinAccount, verificationCode } = appSyncDialog;
    const linkedinAccountId = linkedinAccount?._id;
    if (_.isNil(linkedinAccountId)) return;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, verifyPin: true } }));

    const res = await verifyLinkedinAccountExternal(
      token,
      linkedinAccountId,
      mode === 'approved' ? { action: 'approved' } : verificationCode
    );

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, verifyPin: false } }));

    const checkInterval = 1 * 1000;

    if (!res.error) {
      setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.PROCESSING, verificationCode: '' }));

      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountByIdExternal(token, linkedinAccountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccount((prev) => ({ ...prev!, status: resLinkedinAccount.status }));
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (normalizeAccountStatus(resLinkedinAccount.status)) {
            case LinkedinAccountStatuses.PENDING:
            case LinkedinAccountStatuses.TWO_FA:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              fetchCaptchaForAppSync(linkedinAccountId);
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                linkedinContracts: resLinkedinAccount.linkedinContracts,
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.WRONG_CREDENTIALS }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.WRONG_PIN }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.TIMED_OUT_CAPTCHA }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.ONBOARDING_INCOMPLETE }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.TWO_FA_NOT_ENABLED }));
              break;
            default:
              setAppSyncDialog((prev) => ({ ...prev, activeStepIndex: -1, error: AppSyncErrors.UNKNOWN }));
              break;
          }
        } else {
          if (resLinkedinAccount.error.code === 401 && resLinkedinAccount.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
            handleOpenTokenExpiredDialog();
          } else {
            handleOpenErrorDialog(resLinkedinAccount?.error, '/api/linkedinAccounts/:id', 'get');
          }
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else {
        handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id/setPin', 'put');
      }
    }
  };

  const fetchCaptchaForAppSync = async (accountId: string) => {
    if (_.isNil(token)) return;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, getCaptcha: true } }));

    const res = await getCaptchaExternal(token, accountId);

    if (!res.error) {
      const captcha = `data:image/png;base64,${Buffer.from(res, 'binary').toString('base64')}`;

      const { fetchThirdPartyScreenshotIntervalId } = appSyncDialog;

      if (!_.isNil(fetchThirdPartyScreenshotIntervalId)) {
        clearInterval(fetchThirdPartyScreenshotIntervalId);
        setAppSyncDialog((prev) => ({
          ...prev,
          fetchThirdPartyScreenshotIntervalId: null,
        }));
      }

      const intervalId: NodeJS.Timeout = setInterval(() => reFetchCaptchaForAppSync(accountId, intervalId), 2 * 1000);

      setAppSyncDialog((prev) => ({
        ...prev,
        loading: { ...prev.loading, getCaptcha: false },
        captcha,
        markerPosition: null,
        fetchCaptchaIntervalId: intervalId,
      }));
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/api/linkedinAccounts/:id/getCaptcha', 'get');
      }

      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, getCaptcha: false } }));
    }
  };

  const reFetchCaptchaForAppSync = async (accountId: string, intervalId: any) => {
    if (_.isNil(token)) return;

    const res = await getCaptchaExternal(token, accountId);

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const captcha = `data:image/png;base64,${Buffer.from(res, 'binary').toString('base64')}`;
      setAppSyncDialog((prev) => ({ ...prev, captcha }));
    } else {
      if (!_.isNil(intervalId)) {
        clearInterval(intervalId);
      }

      setAppSyncDialog((prev) => ({
        ...prev,
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
        captcha: null,
        markerPosition: null,
        fetchCaptchaIntervalId: null,
      }));

      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountByIdExternal(token, accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccount((prev) => ({ ...prev!, status: resLinkedinAccount.status }));
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (normalizeAccountStatus(resLinkedinAccount.status)) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.TWO_FA }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: resLinkedinAccount.foundContractNames,
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
              }));
              break;
          }
        } else {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          handleOpenErrorDialog(resLinkedinAccount?.error, '/api/linkedinAccounts/:id', 'get');
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    }
  };

  const handleImageClickFromAppSync = (event: React.MouseEvent) => {
    const image = event.currentTarget;
    const { top, left, width, height } = image.getBoundingClientRect();
    const { clientX, clientY } = event;
    const x = clientX - left;
    const y = clientY - top;

    const percentageX = x / width;
    const percentageY = y / height;

    setAppSyncDialog((prev) => ({ ...prev, markerPosition: { x, y, percentageX, percentageY } }));
  };

  const handleDeleteMarkerFromAppSync = (event: React.MouseEvent) => {
    event.preventDefault();
    setAppSyncDialog((prev) => ({ ...prev, markerPosition: null }));
  };

  const handleSolveCaptchaFromAppSync = async () => {
    if (_.isNil(token)) return;

    const { linkedinAccount, markerPosition, fetchCaptchaIntervalId } = appSyncDialog;

    if (_.isNil(linkedinAccount) || _.isNil(markerPosition)) return;

    const accountId = linkedinAccount._id;
    const positions = { y: markerPosition.percentageY, x: markerPosition.percentageX };

    if (!_.isNil(fetchCaptchaIntervalId)) {
      clearInterval(fetchCaptchaIntervalId);
    }

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: true }, fetchCaptchaIntervalId: null }));

    const res = await solveCaptchaExternal(token, accountId, positions);
    await new Promise((resolve) => setTimeout(resolve, 3000));

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountByIdExternal(token, accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccount((prev) => ({ ...prev!, status: resLinkedinAccount.status }));
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (normalizeAccountStatus(resLinkedinAccount.status)) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.TWO_FA,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                linkedinContracts: resLinkedinAccount.linkedinContracts,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
                loading: { ...prev.loading, solveCaptcha: false },
              }));
              break;
          }
        } else {
          if (resLinkedinAccount.error.code === 401 && resLinkedinAccount.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
            handleOpenTokenExpiredDialog();
          } else {
            handleOpenErrorDialog(resLinkedinAccount?.error, '/api/linkedinAccounts/:id', 'get');
          }

          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/api/linkedinAccounts/:id/solveCaptcha', 'put');
      }

      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
    }
  };

  const handleSelectContractFromAppSync = (contract: string) => {
    return () => {
      setAppSyncDialog((prev) => ({ ...prev, selectedContract: contract }));
    };
  };

  const handleAddContractFromAppSync = async () => {
    if (_.isNil(token)) return;

    const { linkedinAccount, selectedContract } = appSyncDialog;

    if (_.isNil(linkedinAccount)) return;
    const accountId = linkedinAccount._id;
    if (_.isNil(selectedContract)) return;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, update: true } }));

    const res = await updateLinkedinAccountContractExternal(token, accountId, selectedContract);

    const checkInterval = 1 * 1000;

    if (!res.error) {
      setAppSyncDialog((prev) => ({
        ...prev,
        loading: { ...prev.loading, update: false },
        selectedContract: '',
        linkedinContracts: [],
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
      }));

      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountByIdExternal(token, accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccount((prev) => ({ ...prev!, status: resLinkedinAccount.status }));
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (normalizeAccountStatus(resLinkedinAccount.status)) {
            case LinkedinAccountStatuses.PENDING:
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.TWO_FA }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
              }));
              break;
          }
        } else {
          if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
            handleOpenTokenExpiredDialog();
          } else {
            handleOpenErrorDialog(resLinkedinAccount?.error, '/api/linkedinAccounts/:id', 'get');
          }

          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else {
        handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id', 'put');
      }

      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, update: false } }));
    }
  };

  const handleEditAndSynchronizeLiAccount = async () => {
    if (_.isNil(token)) return;

    const { linkedinAccount, selectedCountry, selectedCity, userName, password, selectedLinkedinProduct } = appSyncDialog;

    if (_.isNil(linkedinAccount)) return;
    const accountId = linkedinAccount._id;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, update: true } }));

    const res = await updateLinkedinAccountExternal(
      token,
      accountId,
      selectedCountry.value,
      selectedCity.value,
      userName,
      password,
      selectedLinkedinProduct
    );

    if (!res.error) {
      setAppSyncDialog((prev) => ({
        ...prev,
        loading: { ...prev.loading, update: false },
        userName: '',
        password: '',
      }));
      handleSynchronizeLinkedinAccountFromAppSync(linkedinAccount);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else {
        handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id', 'put');
      }

      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, update: false } }));
    }
  };

  const handleReSynchronizeLiAccount = () => {
    const { linkedinAccount } = appSyncDialog;

    if (_.isNil(linkedinAccount)) return;

    handleSynchronizeLinkedinAccountFromAppSync(linkedinAccount);
  };

  const fetch2FAScreenshotForAppSync = async (accountId: string) => {
    if (_.isNil(token)) return;

    const res = await get2FAScreenshotExternal(token, accountId);

    if (!res.error) {
      const twoFaScreenshot = `data:image/png;base64,${Buffer.from(res, 'binary').toString('base64')}`;

      const intervalId: NodeJS.Timeout = setInterval(() => reFetch2FAScreenshotForAppSync(accountId, intervalId), 5 * 1000);

      setAppSyncDialog((prev) => ({
        ...prev,
        twoFaScreenshot,
        fetch2FAScreenshotIntervalId: intervalId,
      }));
    }
  };

  const reFetch2FAScreenshotForAppSync = async (accountId: string, intervalId: any) => {
    if (_.isNil(token)) return;

    const res = await get2FAScreenshotExternal(token, accountId);

    if (!res.error) {
      const twoFaScreenshot = `data:image/png;base64,${Buffer.from(res, 'binary').toString('base64')}`;
      setAppSyncDialog((prev) => ({ ...prev, twoFaScreenshot }));
    } else {
      if (!_.isNil(intervalId)) {
        clearInterval(intervalId);
      }
      setAppSyncDialog((prev) => ({
        ...prev,
        twoFaScreenshot: null,
        fetch2FAScreenshotIntervalId: null,
      }));
    }
  };

  const fetchThirdPartyScreenshotForAppSync = async (accountId: string) => {
    if (_.isNil(token)) return;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, getThirdPartyScreenshot: true } }));

    const res = await getThirdPartyScreenshotExternal(token, accountId);

    if (!res.error) {
      const thirdPartyScreenshot = `data:image/png;base64,${Buffer.from(res, 'binary').toString('base64')}`;

      const { fetchCaptchaIntervalId } = appSyncDialog;

      if (!_.isNil(fetchCaptchaIntervalId)) {
        clearInterval(fetchCaptchaIntervalId);
        setAppSyncDialog((prev) => ({
          ...prev,
          fetchCaptchaIntervalId: null,
        }));
      }

      const intervalId: NodeJS.Timeout = setInterval(() => reFetchThirdPartyScreenshotForAppSync(accountId, intervalId), 2 * 1000);

      setAppSyncDialog((prev) => ({
        ...prev,
        loading: { ...prev.loading, getThirdPartyScreenshot: false },
        thirdPartyScreenshot,
        markerPosition: null,
        fetchThirdPartyScreenshotIntervalId: intervalId,
      }));
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/api/linkedinAccounts/:id/getThirdPartyScreenshot', 'get');
      }
      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, getThirdPartyScreenshot: false } }));
    }
  };

  const reFetchThirdPartyScreenshotForAppSync = async (accountId: string, intervalId: any) => {
    if (_.isNil(token)) return;

    const res = await getThirdPartyScreenshotExternal(token, accountId);

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const thirdPartyScreenshot = `data:image/png;base64,${Buffer.from(res, 'binary').toString('base64')}`;
      setAppSyncDialog((prev) => ({ ...prev, thirdPartyScreenshot }));
    } else {
      if (!_.isNil(intervalId)) {
        clearInterval(intervalId);
      }

      setAppSyncDialog((prev) => ({
        ...prev,
        syncProcessingStatus: SyncProcessingStatuses.PROCESSING,
        thirdPartyScreenshot: null,
        markerPosition: null,
        fetchThirdPartyScreenshotIntervalId: null,
      }));

      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountByIdExternal(token, accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccount((prev) => ({ ...prev!, status: resLinkedinAccount.status }));
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (normalizeAccountStatus(resLinkedinAccount.status)) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.TWO_FA }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.CAPTCHA }));
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: resLinkedinAccount.foundContractNames,
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({ ...prev, syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
              }));
              break;
          }
        } else {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          handleOpenErrorDialog(resLinkedinAccount?.error, '/api/linkedinAccounts/:id', 'get');
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    }
  };

  const handleThirdPartyClickFromAppSync = async () => {
    if (_.isNil(token)) return;

    const { linkedinAccount, markerPosition, fetchThirdPartyScreenshotIntervalId } = appSyncDialog;

    if (_.isNil(linkedinAccount) || _.isNil(markerPosition)) return;

    const accountId = linkedinAccount._id;
    const positions = { y: markerPosition.percentageY, x: markerPosition.percentageX };

    if (!_.isNil(fetchThirdPartyScreenshotIntervalId)) {
      clearInterval(fetchThirdPartyScreenshotIntervalId);
    }

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: true }, fetchThirdPartyScreenshotIntervalId: null }));

    const res = await thirdPartyClickExternal(token, accountId, positions);
    await new Promise((resolve) => setTimeout(resolve, 3000));

    const checkInterval = 1 * 1000;

    if (!res.error) {
      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountByIdExternal(token, accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccount((prev) => ({ ...prev!, status: resLinkedinAccount.status }));
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (normalizeAccountStatus(resLinkedinAccount.status)) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.TWO_FA,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: resLinkedinAccount.foundContractNames,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
          }
        } else {
          if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
            handleOpenTokenExpiredDialog();
          } else {
            handleOpenErrorDialog(resLinkedinAccount?.error, '/api/linkedinAccounts/:id', 'get');
          }
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/api/linkedinAccounts/:id/thirdPartyClick', 'put');
      }
      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
    }
  };

  const handleThirdPartyTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAppSyncDialog((prev) => ({ ...prev, thirdPartyText: event.target.value }));
  };

  const handleSetThirdPartyTextFromAppSync = async () => {
    if (_.isNil(token)) return;

    const { linkedinAccount, thirdPartyText } = appSyncDialog;

    if (_.isNil(linkedinAccount)) return;

    const accountId = linkedinAccount._id;

    setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: true } }));

    const res = await setThirdPartyTextExternal(token, accountId, thirdPartyText);
    await new Promise((resolve) => setTimeout(resolve, 3000));

    const checkInterval = 1 * 1000;

    if (!res.error) {
      setAppSyncDialog((prev) => ({ ...prev, thirdPartyText: '' }));

      const checkAccountStatus = async () => {
        const resLinkedinAccount = await getLinkedinAccountByIdExternal(token, accountId);

        if (!resLinkedinAccount.error) {
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setLinkedinAccount((prev) => ({ ...prev!, status: resLinkedinAccount.status }));
          setAppSyncDialog((prev) => ({ ...prev, linkedinAccount: resLinkedinAccount }));

          switch (normalizeAccountStatus(resLinkedinAccount.status)) {
            case LinkedinAccountStatuses.PENDING:
              intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
              break;
            case LinkedinAccountStatuses.TWO_FA:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.TWO_FA,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.CAPTCHA:
              fetchCaptchaForAppSync(resLinkedinAccount._id);
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
              break;
            case LinkedinAccountStatuses.CHOOSE_CONTRACT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.CHOOSE_CONTRACT,
                foundContractNames: resLinkedinAccount.foundContractNames,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.THIRD_PARTY:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              fetchThirdPartyScreenshotForAppSync(resLinkedinAccount._id);
              break;
            case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
              setAppSyncDialog((prev) => ({
                ...prev,
                syncProcessingStatus: SyncProcessingStatuses.THIRD_PARTY_TEXT,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.SYNCHRONIZED:
              increaseActiveStepIndex();
              setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
              break;
            case LinkedinAccountStatuses.WRONG_CREDENTIALS:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_CREDENTIALS,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.WRONG_PIN:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.WRONG_PIN,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TIMED_OUT_CAPTCHA,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.ONBOARDING_INCOMPLETE,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.TWO_FA_NOT_ENABLED,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
            default:
              setAppSyncDialog((prev) => ({
                ...prev,
                activeStepIndex: -1,
                error: AppSyncErrors.UNKNOWN,
                loading: { ...prev.loading, thirdPartyClick: false },
              }));
              break;
          }
        } else {
          if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
            handleOpenTokenExpiredDialog();
          } else {
            handleOpenErrorDialog(resLinkedinAccount?.error, '/api/linkedinAccounts/:id', 'get');
          }
          if (!_.isNil(intervalIdRef.current)) {
            clearInterval(intervalIdRef.current);
          }
          setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, thirdPartyClick: false } }));
        }
      };

      intervalIdRef.current = setInterval(checkAccountStatus, checkInterval);
    } else {
      if (res.error.code === 401 && res.error.message === ErrorMessages.TOKEN_EXPIRED_EXTERNAL) {
        handleOpenTokenExpiredDialog();
      } else {
        handleOpenErrorDialog(res?.error, '/api/linkedinAccounts/:id/thirdPartyClick', 'put');
      }
      setAppSyncDialog((prev) => ({ ...prev, loading: { ...prev.loading, solveCaptcha: false } }));
    }
  };

  /**
   * Account already exists dialog handlers
   */

  const handleOpenAccountAlreadyExistsDialog = (userEmail: string) => {
    setAccountAlreadyExistsDialog({ open: true, userEmail });
  };

  const handleCloseAccountAlreadyExistsDialog = () => {
    setAccountAlreadyExistsDialog({ open: false, userEmail: null });
  };

  /**
   * Logout linkedin account handler
   */

  const startLogoutLoading = (accountId: string) => {
    setLogoutLoading((prev) => [...prev, accountId]);
  };

  const endLogoutLoading = (accountId: string) => {
    setLogoutLoading((prev) => prev.filter((id) => id !== accountId));
  };

  /**
   * Soft cancel of a stuck login — the row's "Abbrechen" while connecting. Stops the job and
   * resets the status, but never logs the device out (that wipes the device trust and forces
   * credentials + 2FA again for every app on the next login).
   */
  const handleCancelLinkedinLogin = (account: LinkedinAccount) => {
    return async () => {
      if (_.isNil(token)) return;

      if (_.isNil(account)) return;
      const accountId = account._id;

      startLogoutLoading(accountId);

      const res = await resetLinkedinAccountExternal(token, accountId);

      if (!res.error) {
        const resLinkedinAccount = await getLinkedinAccountByIdExternal(token, accountId);

        if (!resLinkedinAccount.error) {
          endLogoutLoading(accountId);
          setLinkedinAccount((prev) => ({ ...prev!, status: resLinkedinAccount.status }));
        } else {
          endLogoutLoading(accountId);
          handleOpenErrorDialog(resLinkedinAccount.error, '/api/linkedinAccounts/:id', 'get');
        }
      } else {
        endLogoutLoading(accountId);
        handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id/reset', 'post');
      }
    };
  };

  const handleLogoutLinkedinAccount = (account: LinkedinAccount) => {
    return async () => {
      if (_.isNil(token)) return;

      if (_.isNil(account)) return;
      const accountId = account._id;

      startLogoutLoading(accountId);

      const res = await logoutLinkedinAccountExternal(token, accountId);

      if (!res.error) {
        const resLinkedinAccount = await getLinkedinAccountByIdExternal(token, accountId);

        if (!resLinkedinAccount.error) {
          endLogoutLoading(accountId);
          setLinkedinAccount((prev) => ({ ...prev!, status: resLinkedinAccount.status }));
        } else {
          endLogoutLoading(accountId);
          handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id', 'get');
        }
      } else {
        endLogoutLoading(accountId);
        handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id/logout', 'post');
      }
    };
  };

  const handleOpenManageEmailSendersDialog = (account: LinkedinAccount) => () => {
    setManageEmailSendersDialog({ open: true, account });
  };

  const handleCloseManageEmailSendersDialog = () => setManageEmailSendersDialog({ open: false, account: null });

  const handleEditLegacyEmailCredentials = (account: LinkedinAccount) => {
    handleCloseManageEmailSendersDialog();
    handleOpenEditEmailCredentialsDialog(account)();
  };

  const handleRemoveLegacyEmailCredentials = async (account: LinkedinAccount) => {
    if (!window.confirm(t('delete') + ' ' + (account.emailConfig?.smtp?.username || account.emailConfig?.oauth?.email || account.emailConfig?.gmailOauth?.email || '') + '?')) return;
    const res = await updateLinkedinAccountEmailCredentialsExternal(token!, account._id, EmailAccountTypes.OTHER);
    if (!res.error) {
      fetchLinkedinAccountById(true);
      handleCloseManageEmailSendersDialog();
    } else {
      handleOpenErrorDialog(res.error, '/api/linkedinAccounts/:id/saveEmailConfig', 'post');
    }
  };

  return (
    <>
      <Container className={classes.outerContainer}>
        <div className={classes.container}>
          <div className={classes.tabContainer}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <img className={classes.productGroupLogo} src={paiLogo} alt="booster logo" />
              {_.isNil(accountId) && _.isNil(linkedinAccount) ? (
                <PaiButton text={t('addAccount')} size={'medium'} border={'rounded'} action={handleOpenAppSyncDialog(false)} />
              ) : null}
            </Stack>
          </div>
          <div className={classes.content}>
            {linkedinAccountsLoading ? (
              <PaiDotLoader />
            ) : !_.isNil(linkedinAccount) ? (
              <TableContainer className={classes.table}>
                <Table size={'small'}>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '18%' }}>{t('linkedinAccount')}</TableCell>
                      <TableCell style={{ width: '22%' }}>{t('linkedinEmail')}</TableCell>
                      <TableCell style={{ width: '15%' }}>{t('product')}</TableCell>
                      <TableCell style={{ width: '20%' }}>{t('emailAccount')}</TableCell>
                      <TableCell style={{ width: '25%' }}>{t('accountStatus')}</TableCell>
                      <TableCell style={{ width: 'calc(28px + 16px)' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <LinkedinAccountItem
                      account={linkedinAccount}
                      linkedLicenses={[]}
                      syncLoading={appSyncDialog.loading.sync}
                      logoutLoading={logoutLoading.includes(linkedinAccount?._id)}
                      handleOpenAppSyncAndSynchronizeLiAccount={handleOpenAppSyncAndSynchronizeLiAccount}
                      handleOpenAppSyncAndCorrectAccessData={handleOpenAppSyncAndCorrectAccessData}
                      handleOpenRecruiterParallelLoginDialog={handleOpenRecruiterParallelLoginDialog}
                      handleOpenEditLinkedinAccountGeneralSettingsDialog={handleOpenEditLinkedinAccountGeneralSettingsDialog}
                      handleOpenEditLinkedinAccountLoginInformationDialog={handleOpenEditLinkedinAccountLoginInformationDialog}
                      handleOpenManageEmailSendersDialog={handleOpenManageEmailSendersDialog}
                      handleOpenEditEmailCredentialsDialog={handleOpenEditEmailCredentialsDialog}
                      handleOpenAppSyncAndVerifyPin={handleOpenAppSyncAndVerifyPin}
                      handleOpenAppSyncAndSolveCaptcha={handleOpenAppSyncAndSolveCaptcha}
                      handleOpenAppSyncThirdPartyWebsite={handleOpenAppSyncThirdPartyWebsite}
                      handleLogoutLinkedinAccount={handleLogoutLinkedinAccount}
                      handleCancelLinkedinLogin={handleCancelLinkedinLogin}
                      handleOpenOnboardingIncompleteDialog={handleOpenOnboardingIncompleteDialog}
                      handleOpenFairUsePauseDialog={handleOpenFairUsePauseDialog}
                      handleOpenTwoFaNotEnabledDialog={handleOpenTwoFaNotEnabledDialog}
                    />
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </div>
        </div>
      </Container>

      <AppSyncDialog
        open={appSyncDialog.open}
        product={ProductGroups.HR_BOOSTER}
        handleClose={handleCloseAppSyncDialog}
        isStarterExperience={appSyncDialog.isStarterExperience}
        activeStepIndex={appSyncDialog.activeStepIndex}
        loading={appSyncDialog.loading}
        syncProcessingStatus={appSyncDialog.syncProcessingStatus}
        syncError={appSyncDialog.error}
        selectedLinkedinProduct={appSyncDialog.selectedLinkedinProduct}
        allCountryCodes={allCountryCodes}
        selectedCountry={appSyncDialog.selectedCountry}
        selectedCity={appSyncDialog.selectedCity}
        handleLinkedinProductChange={handleAppSyncLinkedinProductChange}
        handleCountryChange={handleAppSyncCountryChange}
        handleCityChange={handleAppSyncCityChange}
        userName={appSyncDialog.userName}
        password={appSyncDialog.password}
        linkedinAccount={appSyncDialog.linkedinAccount}
        verificationCode={appSyncDialog.verificationCode}
        captcha={appSyncDialog.captcha}
        markerPosition={appSyncDialog.markerPosition}
        twoFaScreenshot={appSyncDialog.twoFaScreenshot}
        thirdPartyScreenshot={appSyncDialog.thirdPartyScreenshot}
        thirdPartyText={appSyncDialog.thirdPartyText}
        linkedinContracts={appSyncDialog.linkedinContracts}
        selectedContract={appSyncDialog.selectedContract}
        increaseActiveStepIndex={increaseActiveStepIndex}
        decreaseActiveStepIndex={decreaseActiveStepIndex}
        handleUserNameChange={handleAppSyncUserNameChange}
        handlePasswordChange={handleAppSyncPasswordChange}
        moveToCampaignBuilder={handleCloseAppSyncDialog}
        handleCreateAndSynchronizeLiAccount={handleCreateAndSynchronizeLiAccount}
        handleVerificationCodeChange={handleAppSyncVerificationCodeChange}
        handleVerifyLinkedinAccountFromAppSync={handleVerifyLinkedinAccountFromAppSync}
        handleImageClick={handleImageClickFromAppSync}
        handleDeleteMarker={handleDeleteMarkerFromAppSync}
        handleSolveCaptcha={handleSolveCaptchaFromAppSync}
        handleThirdPartyClick={handleThirdPartyClickFromAppSync}
        handleThirdPartyTextChange={handleThirdPartyTextChange}
        handleSetThirdPartyTextFromAppSync={handleSetThirdPartyTextFromAppSync}
        handleSelectContract={handleSelectContractFromAppSync}
        handleAddContractFromAppSync={handleAddContractFromAppSync}
        handleEditAndSynchronizeLiAccount={handleEditAndSynchronizeLiAccount}
        handleReSynchronizeLiAccount={handleReSynchronizeLiAccount}
        selectedLicenseId={null}
        handleLicenseSelect={() => {}}
      />
      <AccountAlreadyExistsDialog
        open={accountAlreadyExistsDialog.open}
        userEmail={accountAlreadyExistsDialog.userEmail}
        handleClose={handleCloseAccountAlreadyExistsDialog}
      />
      <EditLinkedinAccountGeneralSettingsDialog
        open={editLinkedinAccountGeneralSettingsDialog.open}
        allCountryCodes={allCountryCodes}
        selectedCountry={editLinkedinAccountGeneralSettingsDialog.selectedCountry}
        selectedCity={editLinkedinAccountGeneralSettingsDialog.selectedCity}
        linkedinProduct={editLinkedinAccountGeneralSettingsDialog.linkedinProduct}
        loading={editLinkedinAccountGeneralSettingsDialog.loading}
        handleCountryChange={handleEditCountryChange}
        handleCityChange={handleEditCityChange}
        handleLinkedinProductChange={handleEditLinkedinProductChange}
        handleClose={handleCloseEditLinkedinAccountGeneralSettingsDialog}
        handleEditLinkedinAccount={handleEditLinkedinAccountGeneralSettings}
      />
      <EditLinkedinAccountLoginInformationDialog
        open={editLinkedinAccountLoginInformationDialog.open}
        userName={editLinkedinAccountLoginInformationDialog.userName}
        password={editLinkedinAccountLoginInformationDialog.password}
        loading={editLinkedinAccountLoginInformationDialog.loading}
        handleUserNameChange={handleEditUserNameChange}
        handlePasswordChange={handleEditPasswordChange}
        handleClose={handleCloseEditLinkedinAccountLoginInformationDialog}
        handleEditLinkedinAccount={handleEditLinkedinAccountLoginInformation}
      />

      <WaitBeforeSyncDialog
        open={waitBeforeSyncDialog.open}
        waitingTime={waitBeforeSyncDialog.waitingTime}
        handleClose={handleCloseWaitBeforeSyncDialog}
      />
      <AccountAlreadySyncedDialog open={accountAlreadySyncedDialog.open} handleClose={handleCloseAccountAlreadySyncedDialog} />
      <EditEmailCredentialsDialog
        open={editEmailCredentialsDialog.open}
        type={editEmailCredentialsDialog.type}
        smtp={editEmailCredentialsDialog.smtp}
        imap={editEmailCredentialsDialog.imap}
        oauth={editEmailCredentialsDialog.oauth}
        error={editEmailCredentialsDialog.error}
        accessRevoked={editEmailCredentialsDialog.accessRevoked}
        loading={editEmailCredentialsDialog.loading}
        handleEmailAccountTypeChange={handleEmailAccountTypeChange}
        handleSmtpEmailCredentialsChange={handleSmtpEmailCredentialsChange}
        handleSmtpEncryptionChange={handleSmtpEncryptionChange}
        handleImapEmailCredentialsChange={handleImapEmailCredentialsChange}
        handleImapEncryptionChange={handleImapEncryptionChange}
        handleOAuthEmailCredentialsChange={handleOAuthEmailCredentialsChange}
        handleClose={handleCloseEditEmailCredentialsDialog}
        handleAcceptEmailCredentials={handleAcceptEmailCredentials}
      />
      <RecruiterParallelLoginDialog
        open={recruiterParallelLoginDialog.open}
        loading={recruiterParallelLoginDialog.loading}
        handleClose={handleCloseRecruiterParallelLoginDialog}
        handleSynchronizeLinkedinAccount={handleReSynchronizeLinkedinAccountFromParallelLogin}
      />
      <OnboardingIncompleteDialog
        open={onboardingIncompleteDialog.open}
        loading={onboardingIncompleteDialog.loading}
        handleClose={handleCloseOnboardingIncompleteDialog}
        handleSynchronizeLinkedinAccount={handleReSynchronizeLinkedinAccountFromOnboardingIncomplete}
      />
      <FairUsePauseDialog open={fairUsePauseDialog.open} handleClose={handleCloseFairUsePauseDialog} />
      <TwoFaNotEnabledDialog
        open={twoFaNotEnabledDialog.open}
        account={twoFaNotEnabledDialog.account}
        handleClose={handleCloseTwoFaNotEnabledDialog}
        handleSynchronizeLinkedinAccount={handleReSynchronizeLinkedinAccountFromTwoFaNotEnabled}
      />
      <TokenExpiredDialog open={tokenExpiredDialog.open} handleClose={handleCloseTokenExpiredDialog} />
      <ErrorDialog />
      <ManageEmailSendersDialog
        open={manageEmailSendersDialog.open}
        token={token!}
        account={manageEmailSendersDialog.account}
        onClose={handleCloseManageEmailSendersDialog}
        onEditLegacy={handleEditLegacyEmailCredentials}
        onRemoveLegacy={handleRemoveLegacyEmailCredentials}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  outerContainer: {
    padding: '16px',
    maxWidth: '1920px',
  },
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 32px)',
    maxHeight: 'calc(960px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
  },
  tabContainer: {
    padding: '16px 32px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  productGroupLogo: {
    height: '39px',
    width: 'auto',
  },
  content: {
    height: '100%',
    padding: '16px 32px',
    overflow: 'auto',
    display: 'flex',
  },
  table: {
    borderRadius: '6px',
    '& th, & td': {
      fontSize: theme.typography.pxToRem(15),
      padding: '8px 16px',
      backgroundColor: '#FFF',
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
    '& tr td:nth-last-of-type(-n+2)': {
      borderLeft: '1px solid #ddd',
    },
    '& tr td:last-child': {
      padding: '8px',
    },
  },
}));
export default LinkedinAccountExternal;
